import {HttpCode} from '../../Constant/HttpCode';
import {
  FETCH_ERROR,
  FETCH_START,
  FETCH_SUCCESS,
  SOMETHING_WENT_WRONG,
} from '../../Constant/ActionTypes';
import {commonSortOrder, handleJwtExpireMsg} from '../../Util/commonUtility';
import Urls from '../../Constant/Urls';
import {httpClient} from '../../Api/client';

const resolveErrorMessage = (error) => {
  let errorMessage = '';
  switch (error?.response?.status) {
    case HttpCode.InternalServerError:
      errorMessage = error?.response?.data?.errorMessage;
      break;
    case HttpCode.NotFound:
      errorMessage = error?.response?.data?.errorMessage;
      break;
    case HttpCode.Unauthorized:
      errorMessage = '';
      break;
    case HttpCode.Forbidden:
      errorMessage = error?.response?.data?.errorMessage;
      break;
    default:
      errorMessage = SOMETHING_WENT_WRONG;
      break;
  }
  return handleJwtExpireMsg(error, errorMessage);
};

/**
 * Admin: fetch all event registrations across events with filters + pagination.
 * Resolves with the response `data` so the calling screen manages its own local state.
 */
export const getRegistrationHistory = (
  pagination = {page: '', pageCount: ''},
  sortField = '',
  sortOrder = '',
  searchQueryData = '',
  // When true, skip the global loader. The Registration History screen manages
  // its own table `loading` state, so the global spinner would just stack a
  // second loader on top of it.
  hideLoader = false,
) => {
  return async (dispatch) => {
    if (!hideLoader) dispatch({type: FETCH_START});
    try {
      const data = await httpClient.get(
        `${Urls.REGISTRATION_HISTORY_GET_ALL}?page=${
          pagination.page
        }&pageCount=${pagination.pageCount}&sortOrder=${commonSortOrder(
          sortOrder,
        )}&sortField=${sortField}&${searchQueryData || ''}`,
      );
      if (data?.status === HttpCode.Ok) {
        if (!hideLoader) dispatch({type: FETCH_SUCCESS});
        return data?.data?.data;
      }
    } catch (error) {
      if (!hideLoader)
        dispatch({type: FETCH_ERROR, payload: resolveErrorMessage(error)});
    }
    return null;
  };
};

/**
 * Admin: fetch the transaction receipt (all attendees for a transaction) so the same
 * ReceiptComponent used on "My Events" can be rendered. Resolves with the response data.
 */
export const getReceiptByTransactionId = (transactionId) => {
  return async (dispatch) => {
    dispatch({type: FETCH_START});
    try {
      const data = await httpClient.get(
        Urls.GET_ALL_ATTENDEE_TRANSACTION_ID.replace(
          '#{transactionId}',
          transactionId,
        ),
      );
      if (data?.status === HttpCode.Ok) {
        dispatch({type: FETCH_SUCCESS});
        return data?.data?.data;
      }
    } catch (error) {
      dispatch({type: FETCH_ERROR, payload: resolveErrorMessage(error)});
    }
    return null;
  };
};

/**
 * Admin: fetch a single member's credit history (reconstructed from registration data).
 */
export const getUserCreditHistory = (userId) => {
  return async (dispatch) => {
    dispatch({type: FETCH_START});
    try {
      const data = await httpClient.get(
        Urls.USER_CREDIT_HISTORY.replace('#{userId}', userId),
      );
      if (data?.status === HttpCode.Ok) {
        dispatch({type: FETCH_SUCCESS});
        return data?.data?.data;
      }
    } catch (error) {
      dispatch({type: FETCH_ERROR, payload: resolveErrorMessage(error)});
    }
    return null;
  };
};

/**
 * Admin: manually adjust a member's refund credit.
 * `payload` = { amount (signed), note? }. Resolves with the response data (new balance)
 * on success, or null on failure (error surfaced via the global error toast).
 */
export const updateUserCredit = (userId, payload) => {
  return async (dispatch) => {
    dispatch({type: FETCH_START});
    try {
      const data = await httpClient.patch(
        Urls.USER_UPDATE_CREDIT.replace('#{userId}', userId),
        payload,
      );
      if (data?.status === HttpCode.Ok) {
        dispatch({type: FETCH_SUCCESS});
        return data?.data?.data;
      }
    } catch (error) {
      const errorMessage = resolveErrorMessage(error);
      dispatch({type: FETCH_ERROR, payload: errorMessage});
      return {error: errorMessage};
    }
    return null;
  };
};
